import React, { useCallback, useEffect, useMemo, useState } from "react";
import TableList from "../../generic/elements/tablelist/TableList";
import SearchInput from "../../generic/elements/search/SearchInput";
import "../patientlist/patientlist.css";
import TableColumns from "./TableColumns";
import IntentType from "../../../intentexecutor/utils/IntentType";
import {
  workforceListPageState,
  workforceListHasMoreDataState,
  workforceListTableDataState,
  workforceListTotalCount,
  workforceListSearchQuery,
} from "../../../components/recoil/workforceListRecoil";
import { useRecoilState, useRecoilValue } from "recoil";
import MemoryType from "../../../service/memory/enum/MemoryType";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import { executeIntent } from "../../utils/IntentExecutor";
import { Col, Row } from "antd";
import BackButton from "../../generic/elements/backbutton/BackButton";
import CustomScrollbar from "../../../components/generic/scrollbar/CustomScrollbar";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import ConfigurationConstants from "../../utils/configurationconstants";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import AddEditWorkforceModal, {
  workforceDataUpdateResponse,
} from "../workforce/AddEditWorkforceModal";
import { teamList } from "../../../components/recoil/teamsRecoil";
import { useObservable } from "../../generic/hooks/useObservable";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import { getFullName } from "../../careprovider/visits/utils";
import { getCurrentTimeZone, getDateString } from "../../../utils/dateutils";
import apiService from "../../../service/apiservice/apiService";
import SearchAndSelect from "../../../components/generic/search/SearchAndSelect";
import SelectTypeOFServices from "../requestforvolunteeringservice/components/SelectTypeOFServices";

const STATIC_HEADERS = [
  "Name",
  "Role",
  "Status",
  "Address",
  "Email",
  "Phone",
  "Anniversary",
  "Birthdate",
  "Total Hours",
  "YTD",
  "MTD",
  "Total Mileage",
  "YTD Mileage",
  "Date of Last Activity",
];

const WorkforceListComponent = () => {
  const [tableData, setTableData] = useRecoilState(workforceListTableDataState);
  const [hasMoreData, setHasMoreData] = useRecoilState(
    workforceListHasMoreDataState,
  );
  const teams = useRecoilValue(teamList);
  const [page, setPage] = useRecoilState(workforceListPageState);
  const [totalCount, setTotalCount] = useRecoilState(workforceListTotalCount);
  const configuration = useRecoilValue(currentConfiguration);
  const [openAddWorkforceModal, setOpenAddWorkforceModal] = useState(false);
  const [showAddVolunteerButton, setShowAddVolunteerButton] = useState(false);
  const [searchQuery, setSearchQuery] = useRecoilState(workforceListSearchQuery);
  const [tableLoading, setTableLoading] = useState(false);
  const { columns } = TableColumns();
  const userJson = useReadMemory(MemoryType.USER);
  const [reload, setReload] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [workforceStatus, setWorkforceStatus] = useState([
    { label: "Active", value: "Active" },
  ]);
  const [services, setServices] = useState([]);
  const [options, setOptions] = useState([]);
  useObservable(workforceDataUpdateResponse, setReload);

  const searchPayload = useMemo(
    () => ({
      page: 1,
      query: searchQuery,
      organizationIdentifier: userJson?.organizationIdentifier,
      filters: {
        status: ["Onboarded"],
        workforceStatus: workforceStatus.map((status) => status.value),
        services: services.map((service) => service?.label),
      },
    }),
    [searchQuery, userJson?.organizationIdentifier, workforceStatus, services],
  );

  const handleExport = async () => {
    if (!tableData?.length) return;
    setExportLoading(true);
    try {
      const payload = {
        timestamp: Date.now(),
        workforceIdentifier: tableData?.map(
          (workforce) => workforce?.userIdentifier,
        ),
        timezone: getCurrentTimeZone(),
      };
      const workforceMap = await apiService.post(
        "niramayafoundation/api/v1/workforcesummary",
        payload,
      );

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Workforce");

      // Freeze header row
      worksheet.views = [{ state: "frozen", ySplit: 1 }];

      // Add header row
      const headerRow = worksheet.addRow(STATIC_HEADERS);
      // Style header row
      headerRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = {
          horizontal: "left",
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Add tableData rows
      tableData.forEach((workforce) => {
        const metadata = JSON.parse(workforce?.metadata || "{}");
        const summary = workforceMap?.[workforce?.userIdentifier];
        if (!summary) {
          return;
        }
        const {
          totalDirectHours,
          totalIndirectHours,
          yearlyDirectHours,
          yearlyIndirectHours,
          monthlyDirectHours,
          monthlyIndirectHours,
          totalDirectMileage,
          totalIndirectMileage,
          yearlyDirectMileage,
          yearlyIndirectMileage,
          lastActivityTimestamp,
        } = summary;
        const baseRow = [
          getFullName(workforce?.givenName, workforce?.familyName) || "--",
          workforce?.jobTitle,
          metadata?.workforceStatus || "--",
          workforce?.completeAddress,
          workforce?.email || "--",
          workforce?.telephone || "--",
          workforce?.anniversary ? getDateString(workforce?.anniversary) : "--",
          workforce?.birthDate ? getDateString(workforce?.birthDate) : "--",
          totalDirectHours + totalIndirectHours || 0,
          yearlyDirectHours + yearlyIndirectHours || 0,
          monthlyDirectHours + monthlyIndirectHours || 0,
          totalDirectMileage + totalIndirectMileage || 0,
          yearlyDirectMileage + yearlyIndirectMileage || 0,
          lastActivityTimestamp ? getDateString(lastActivityTimestamp) : "--",
        ];

        worksheet.addRow(baseRow);
      });

      // Auto column width
      worksheet.columns.forEach((column) => {
        let maxLength = 10;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const length = cell.value ? cell.value.toString().length : 0;
          if (length > maxLength) {
            maxLength = length;
          }
        });
        column.width = maxLength + 2;
      });

      // Generate excel buffer
      const buffer = await workbook.xlsx.writeBuffer();

      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .replace("T", "_")
        .slice(0, 19);

      // Download
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `Workforce_Data_${timestamp}.xlsx`);
    } finally {
      setExportLoading(false);
    }
  };

  const handleAddWorkforceClick = () => {
    setOpenAddWorkforceModal(!openAddWorkforceModal);
  };

  const fetchWorkforceListData = useCallback(async () => {
    setTableLoading(true);
    const payload = {
      page: 1,
      organizationIdentifier: userJson?.organizationIdentifier,
      filters: {
        status: ["Onboarded"],
        workforceStatus: workforceStatus.map((status) => status.value),
        services: services.map((service) => service?.label),
      },
    };
    const responseData = await executeIntent(
      IntentType.GET_ALL_WORKFORCE,
      payload,
    );
    setTableData(Array.isArray(responseData?.workforceList) ? responseData?.workforceList : []);
    setTotalCount(responseData?.totalCount || 0);
    setTableLoading(false);
    if (responseData?.workforceList?.length < 100) {
      setHasMoreData(false);
    } else {
      setPage(2);
      setHasMoreData(true);
    }
  }, [userJson?.organizationIdentifier, workforceStatus, services, setTableData, setTotalCount, setHasMoreData, setPage]);

  const fetchMoreData = useCallback(async () => {
    const payload = {
      page: page,
      organizationIdentifier: userJson?.organizationIdentifier,
      filters: {
        status: ["Onboarded"],
        workforceStatus: workforceStatus.map((status) => status.value),
        services: services.map((service) => service?.label),
      },
    };
    if (hasMoreData) {
      const responseData = await executeIntent(
        IntentType.GET_ALL_WORKFORCE,
        payload,
      );
      if (responseData?.workforceList?.length > 0) {
        setTableData((prevData) => [
          ...prevData,
          ...responseData?.workforceList,
        ]);
        setTotalCount(responseData?.totalCount);
        setPage(page + 1);
      } else {
        setHasMoreData(false);
      }
    }
  }, [page, userJson?.organizationIdentifier, workforceStatus, services, hasMoreData, setTableData, setTotalCount, setPage, setHasMoreData]);

  const fetchSearchedData = useCallback(async () => {
    setTableLoading(true);
    const responseData = await executeIntent(
      IntentType.SEARCH_WORKFORCE,
      searchPayload,
    );
    setTableData(Array.isArray(responseData?.workforceList) ? responseData?.workforceList : []);
    setTotalCount(responseData?.totalCount || 0);
    setTableLoading(false);
    if (responseData?.workforceList?.length < 100) {
      setHasMoreData(false);
    } else {
      setPage(2);
      setHasMoreData(true);
    }
  }, [
    searchPayload,
    setTableData,
    setTableLoading,
    setHasMoreData,
    setPage,
    setTotalCount,
  ]);

  useEffect(() => {
    // access control for add volunteer button
    const configurationShowAddVolunteerButton =
      configuration[
        ConfigurationConstants.FEATUREACCESS_WORKFORCE_ADD_WORKFORCE
      ];
    setShowAddVolunteerButton(configurationShowAddVolunteerButton?.visibility);
  }, [configuration]);

  useEffect(() => {
    if (searchQuery.length > 0) {
      fetchSearchedData();
    } else if (searchQuery.length === 0) {
      fetchWorkforceListData();
    }
  }, [
    searchQuery,
    teams,
    reload,
    workforceStatus,
    fetchSearchedData,
    fetchWorkforceListData,
  ]);

  useEffect(() => {
    const getOptions = async () => {
      const payload = {
        configurationKey: ["niramaya.add.patient.options"],
      };

      const result = await executeIntent(
        IntentType.GET_CONFIGURATION,
        payload,
        {},
      );
      const raw = result?.[0]?.configurationValue;
      const options = raw && JSON.parse(raw);
      setOptions(options?.workforceStatus?.menuItems);
    };

    getOptions();
  }, []);

  return (
    <>
      <section className="section-content">
        <div className="fixed-content">
          <div className="form-renderer-back-button workforce-list-header-row">
            <div className="workforce-list-header-title">
              <BackButton />
              <p>My Team</p>
            </div>
            <div className="workforce-list-total-count">
              <span className="workforce-list-total-count-label">
                Total Count:
              </span>
              <span className="workforce-list-total-count-value">
                {totalCount}
              </span>
            </div>
          </div>

          <div className="careplan-wrapper row" style={{ marginBlock: 10 }}>
            <div className="col-12 col-md-4 careplan-search-wrapper">
              <SearchInput
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                cssClass="searchFilterContainer"
                searchPlaceholder={"Search Team Member..."}
              />
            </div>

            <div className="col-12 col-md-4">
              <div className="careplan-select-wrapper">
                <SearchAndSelect
                  mode="multiple"
                  selectedValues={workforceStatus}
                  placeholder="Click here to select status"
                  setValue={setWorkforceStatus}
                  style={{
                    width: "100%",
                  }}
                  options={options}
                  selectOptionType="Simple"
                  noDebounceSearch={true}
                />
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div className="careplan-select-wrapper">
                <SelectTypeOFServices
                  typesOfServicesSelected={services}
                  setTypesOfServicesSelected={setServices}
                  showLabel={false}
                />
              </div>
            </div>

            <Row style={{ gap: 4 }} justify="end">
              <Col>
                {showAddVolunteerButton && (
                  <PrimaryButton
                    buttonText="Invite Team Member"
                    handleOnClick={handleAddWorkforceClick}
                  />
                )}
              </Col>
              <Col>
                <PrimaryButton
                  buttonText="Export"
                  handleOnClick={handleExport}
                  loading={exportLoading}
                  buttonDisable={!tableData?.length}
                />
              </Col>
            </Row>
          </div>
        </div>
        <div className="workforce-list-table">
          <CustomScrollbar>
            {tableData.length !== undefined && (
              <TableList
                data={tableData}
                columns={columns}
                hasMoreData={hasMoreData}
                searchQuery={searchQuery}
                tableLoading={tableLoading}
                fetchMoreData={fetchMoreData}
              />
            )}
          </CustomScrollbar>
        </div>
      </section>

      {openAddWorkforceModal && (
        <AddEditWorkforceModal
          openAddWorkforceModal={openAddWorkforceModal}
          setOpenAddWorkforceModal={setOpenAddWorkforceModal}
          showAdditionalFields={false}
        />
      )}
    </>
  );
};

export default WorkforceListComponent;
